import React, { useMemo } from 'react';
import { useUnitats } from '../../hooks/useUnitats';
import useFetch from '../../hooks/useFetch';
import * as S from './CategoryGrid.styles';
import SlideshowThumbnail from './SlideshowThumbnail';
import { extractErrorMessage } from '../../utils/errorHandling';
import { ErrorMessage, LoadingGif, FullPageLoader } from '../Shared/CommonStyles';

interface CategoryGridProps {
    onCategorySelect: (category: string) => void;
    canSeeSecret: boolean;
    canSeeClassificar: boolean;
}

interface MediaItem {
    thumbnail_path: string;
}

const CategoryCardItem = ({ category, label, isSecret = false, onlyProvisional = false, onClick }: { category: string, label: string, isSecret?: boolean, onlyProvisional?: boolean, onClick: () => void }) => {
    // Random interval between 5000ms and 10000ms (inclusive)
    const [interval] = React.useState(() => Math.floor(Math.random() * 5001) + 5000);

    let url = `/api/galeria/gallery-items/random?limit=5`;
    if (isSecret) {
        url += '&onlySecret=true';
    } else if (onlyProvisional) {
        url += '&onlyProvisional=true';
    } else if (category !== 'tots') {
        url += `&unitat=${encodeURIComponent(category)}`;
    }

    const queryKey = ['category-thumbnails', category, isSecret ? 'secret' : onlyProvisional ? 'provisional' : 'public'];

    const { data, isLoading } = useFetch<{ mediaItems: MediaItem[] }>({
        queryKey,
        url,
    });

    const images = useMemo(() => {
        if (!data?.mediaItems) return [];
        return data.mediaItems.map((item) => item.thumbnail_path);
    }, [data]);

    return (
        <S.CategoryCard onClick={onClick}>
             <div style={{ width: '100%', aspectRatio: '4/3', position: 'relative' }}>
                <SlideshowThumbnail images={images} interval={interval} isLoading={isLoading} />
             </div>
             <S.CategoryTitle>{label}</S.CategoryTitle>
        </S.CategoryCard>
    );
};

const CategoryGrid: React.FC<CategoryGridProps> = ({ onCategorySelect, canSeeSecret, canSeeClassificar }) => {
    const { unitats, isFetchingUnitats, fetchUnitatsError } = useUnitats();

    if (isFetchingUnitats) {
        return <FullPageLoader><LoadingGif size="large" /></FullPageLoader>;
    }

    if (fetchUnitatsError) {
        return <ErrorMessage>Error: {extractErrorMessage(fetchUnitatsError)}</ErrorMessage>;
    }

    return (
        <S.Grid>
            {/* Tots els àlbums */}
            <CategoryCardItem 
                category="tots" 
                label="Tots els àlbums" 
                onClick={() => onCategorySelect('tots')} 
            />

            {/* Agrupament */}
            <CategoryCardItem 
                category="agrupament" 
                label="Agrupament" 
                onClick={() => onCategorySelect('agrupament')} 
            />
            
            {/* Unitats */}
            {unitats && unitats.map(unitat => (
                <CategoryCardItem 
                    key={unitat._id}
                    category={unitat.abreviacio.toLowerCase()} 
                    label={unitat.abreviacio}
                    onClick={() => onCategorySelect(unitat.abreviacio.toLowerCase())} 
                />
            ))}

            {/* Per classificar — visible to elevated roles only (not Familiar) */}
            {canSeeClassificar && (
                <CategoryCardItem 
                    category="classificar" 
                    label="Per classificar" 
                    onlyProvisional={true}
                    onClick={() => onCategorySelect('classificar')} 
                />
            )}

            {/* Secret / Només Caps */}
            {canSeeSecret && (
                <CategoryCardItem 
                    category="secret" 
                    label="Només Caps" 
                    isSecret={true}
                    onClick={() => onCategorySelect('secret')} 
                />
            )}
        </S.Grid>
    );
};

export default CategoryGrid;
